import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import useRecorder from "./useRecorder";
import { uploadAudio } from "../Redux/Action/apiAction";

/**
 * Wraps useRecorder with an "upload as soon as recording stops" flow.
 *
 * Lifecycle (phase):
 *   idle       – nothing recorded yet for this question
 *   recording  – mic is live
 *   uploading  – recording stopped, blob is being sent to the backend
 *   ready      – backend returned an audio_url; playback + submit are allowed
 *   error      – upload failed; user can retry the upload or re-record
 *
 * The user can re-record any number of times. Each stop uploads a fresh file
 * and replaces `uploadedUrl`. `uploadedUrl` always holds the LATEST successful
 * upload, which is what the caller sends to the websocket on submit.
 */
export default function useAudioAnswer({ assignmentId, filePrefix = "recording" }) {
  const dispatch = useDispatch();
  const {
    startRecording,
    stopRecording,
    reset: resetRecorder,
    isRecording,
    audioBlob,
    audioMimeType,
    durationSeconds,
  } = useRecorder();

  const [phase, setPhase] = useState("idle");
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [uploadedDuration, setUploadedDuration] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  // Blob we've already handed to the uploader (dedupes the effect below).
  const handledBlobRef = useRef(null);
  // Metadata of the question currently being recorded.
  const questionRef = useRef(null);
  // Bumped on every begin/record-again so a late upload response from a
  // superseded take is ignored.
  const genRef = useRef(0);
  // durationSeconds settles asynchronously — keep the latest for the uploader.
  const durationRef = useRef(0);
  useEffect(() => {
    durationRef.current = durationSeconds;
  }, [durationSeconds]);

  const doUpload = useCallback(
    async (blob) => {
      const q = questionRef.current;
      const gen = genRef.current;

      if (!blob || blob.size === 0) {
        setPhase("error");
        setUploadError("No audio was captured. Please record again.");
        return;
      }
      if (!q) return;

      setPhase("uploading");
      setUploadError(null);

      try {
        // The upload backend only accepts audio/webm, but iOS Safari can only
        // record audio/mp4. Relabel the part as webm so it passes the backend's
        // type gate — the server's transcoder sniffs the real content anyway.
        // TODO: remove once the backend accepts audio/mp4 / .m4a directly.
        const realType = blob.type || audioMimeType;
        const uploadBlob =
          realType === "audio/webm"
            ? blob
            : new Blob([blob], { type: "audio/webm" });

        const formData = new FormData();
        formData.append(
          "audio_file",
          uploadBlob,
          `${filePrefix}_${q.id}_${Date.now()}.webm`
        );
        formData.append("assessment_id", assignmentId);
        formData.append("question_id", q.id);
        formData.append("question_type", q.content?.question_type || "");
        formData.append("assignment_question_id", q.assignment_question_id);

        const res = await dispatch(uploadAudio(formData));

        if (gen !== genRef.current) return; // a newer take replaced this one

        if (res?.success && res.payload?.audio_url) {
          setUploadedUrl(res.payload.audio_url);
          setUploadedDuration(
            res.payload.duration_seconds ?? durationRef.current
          );
          setPhase("ready");
        } else {
          setPhase("error");
          setUploadError("Upload failed. Please retry.");
        }
      } catch (err) {
        if (gen !== genRef.current) return;
        console.error("Audio upload error", err);
        setPhase("error");
        setUploadError("Upload failed. Please retry.");
      }
    },
    [assignmentId, filePrefix, dispatch, audioMimeType]
  );

  // Auto-upload the moment the recorder produces a new blob.
  useEffect(() => {
    if (!audioBlob) return;
    if (handledBlobRef.current === audioBlob) return;
    handledBlobRef.current = audioBlob;
    doUpload(audioBlob);
  }, [audioBlob, doUpload]);

  const beginRecording = useCallback(
    (question) => {
      genRef.current += 1;
      questionRef.current = question;
      handledBlobRef.current = null;
      setUploadedUrl(null);
      setUploadedDuration(0);
      setUploadError(null);
      resetRecorder();
      setPhase("recording");
      startRecording();
    },
    [resetRecorder, startRecording]
  );

  // Stop the mic. The auto-upload effect takes it from here.
  const finishRecording = useCallback(() => {
    stopRecording();
    setPhase((p) => (p === "recording" ? "uploading" : p));
  }, [stopRecording]);

  const retryUpload = useCallback(() => {
    if (audioBlob) doUpload(audioBlob);
  }, [audioBlob, doUpload]);

  // Discard the current take and go back to a clean slate.
  const recordAgain = useCallback(() => {
    genRef.current += 1;
    handledBlobRef.current = null;
    setUploadedUrl(null);
    setUploadedDuration(0);
    setUploadError(null);
    setPhase("idle");
    resetRecorder();
  }, [resetRecorder]);

  return {
    phase,
    isRecording,
    hasRecording: phase === "uploading" || phase === "ready" || phase === "error",
    uploadedUrl,
    uploadedDuration,
    uploadError,
    beginRecording,
    finishRecording,
    retryUpload,
    recordAgain,
    stopRecording,
  };
}
